import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  useTheme,
  IconButton,
  Tooltip,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Collapse,
  Theme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { 
  Mail, 
  FileText, 
  Zap, 
  Globe, 
  Layers, 
  Link, 
  Settings, 
  MessageCircle, 
  HelpCircle, 
  BarChart3,
  Sliders,
  ArrowLeft,
  Save,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { useToast } from 'context/ToastContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { LayoutGrid } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  selectApplicationScopes, 
  selectApplicationScopeLoading, 
  selectApplicationScopeError,
  setLoading,
  setApplicationScopes,
  setError,
  clearError
} from 'store/dataReducers/applicationScopeSlice';
import { getApplicationScope, saveApplicationScope } from 'apis/applicationScopeAPI';
import { PermissionGate } from 'components/access/PermissionGate';
import { UI_PAGES } from 'framework/access/UiRegistry';
import { ApplicationScopeSaveRequest, FuelTypeAllowance, SkippedDetail } from 'types/apis/applicationScopeInterfaces';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';

const SMS_APP_IDS = new Set(['smart-sms', 'sms-alerts']);
const isSmsApp = (appId: string) => SMS_APP_IDS.has(appId);
const smsScopeKey = (appId: string, name: string) => `${appId}::${name}`;

const SMS_SCOPE_COLUMNS: { key: 'API' | 'NON_API'; label: string }[] = [
  { key: 'API', label: 'API' },
  { key: 'NON_API', label: 'Non API' }
];

function togglesFromSmsSelectedFor(selected: Partial<FuelTypeAllowance> | undefined): { API: boolean; NON_API: boolean } {
  return {
    API: Boolean(selected?.api),
    NON_API: Boolean(selected?.non_api)
  };
}

function normalizeSmsAllowedScope(v: unknown): 'API' | 'NON_API' | 'BOTH' | null {
  if (v == null || v === '') return null;
  const u = String(v).toUpperCase();
  if (u === 'API' || u === 'NON_API' || u === 'BOTH') return u;
  return null;
}

function isSmsChannelAllowed(allowedFor: FuelTypeAllowance | undefined, columnKey: 'API' | 'NON_API'): boolean {
  if (!allowedFor) return false;
  // Prefer explicit booleans on allowed_for (e.g. { api: true, non_api: true }).
  if (columnKey === 'API' && typeof allowedFor.api === 'boolean') return allowedFor.api;
  if (columnKey === 'NON_API' && typeof allowedFor.non_api === 'boolean') return allowedFor.non_api;
  // Fallback: api_non_api scope (API | NON_API | BOTH).
  const scope = normalizeSmsAllowedScope(allowedFor.api_non_api);
  if (scope === 'BOTH') return true;
  if (scope === 'API') return columnKey === 'API';
  if (scope === 'NON_API') return columnKey === 'NON_API';
  return false;
}

const buildScopeSwitchSx = (theme: Theme, isAllowed: boolean) => ({
  width: 38,
  height: 22,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: '2px',
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: isAllowed ? theme.palette.primary.main : theme.palette.action.disabled,
        opacity: 1,
        border: 0,
      },
      '& .MuiSwitch-thumb': {
        color: '#fff',
      },
    },
    '&.Mui-disabled': {
      '& .MuiSwitch-thumb': {
        color: theme.palette.action.disabled,
      },
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.action.disabledBackground,
        opacity: 0.3,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: theme.palette.primary.main,
      border: '6px solid #fff',
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 18,
    height: 18,
    color: isAllowed ? '#fff' : theme.palette.action.disabled,
  },
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    backgroundColor: isAllowed ? '#E9E9EA' : theme.palette.action.disabledBackground,
    opacity: isAllowed ? 1 : 0.3,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
});

type ScopeSwitchCellProps = {
  checked: boolean;
  isAllowed: boolean;
  onChange: () => void;
};

const parseSaveWarning = (warning: string): { source?: string; message: string } => {
  const separatorIndex = warning.indexOf(': ');
  if (separatorIndex > 0) {
    return {
      source: warning.slice(0, separatorIndex),
      message: warning.slice(separatorIndex + 2),
    };
  }
  return { message: warning };
};

const ScopeSwitchCell: React.FC<ScopeSwitchCellProps> = ({ checked, isAllowed, onChange }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Switch
        checked={checked}
        onChange={onChange}
        disabled={!isAllowed}
        size="small"
        sx={buildScopeSwitchSx(theme, isAllowed)}
      />
    </Box>
  );
};

export const ApplicationScopePage = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const dispatch = useDispatch();
  const toast = useToast();
  
  // Redux state
  const applicationScopes = useSelector(selectApplicationScopes);
  const loading = useSelector(selectApplicationScopeLoading);
  const error = useSelector(selectApplicationScopeError);
  const projectId = useSelector(selectSelectedPilotId);
  const [expandedApps, setExpandedApps] = useState<string[]>(['email-smart-alerts']);
  const [expandedFeatures, setExpandedFeatures] = useState<Set<string>>(new Set());
  const [expandedWidgets, setExpandedWidgets] = useState<Set<string>>(new Set());
  const [featureToggles, setFeatureToggles] = useState<Record<string, Record<string, boolean>>>({});
  const [widgetToggles, setWidgetToggles] = useState<Record<string, Record<string, boolean>>>({});
  const [viewToggles, setViewToggles] = useState<Record<string, Record<string, boolean>>>({});
  const [smsFeatureToggles, setSmsFeatureToggles] = useState<Record<string, { API: boolean; NON_API: boolean }>>({});
  const [smsWidgetToggles, setSmsWidgetToggles] = useState<Record<string, { API: boolean; NON_API: boolean }>>({});
  const [smsViewToggles, setSmsViewToggles] = useState<Record<string, { API: boolean; NON_API: boolean }>>({});
  const [allowedMeasurementTypes, setAllowedMeasurementTypes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveWarnings, setSaveWarnings] = useState<string[]>([]);
  const [skippedDetails, setSkippedDetails] = useState<SkippedDetail[]>([]);
  const [communicationEventsError, setCommunicationEventsError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.APP_SCOPE_LOADED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch application scope data based on user attributes (from project/session settings)
  useEffect(() => {
    const fetchApplicationScope = async () => {
      dispatch(setLoading(true));
      try {
        const queryParams = projectId ? { project_id: projectId } : {} as any;
        
        const response = await getApplicationScope(rc, queryParams);
        console.log('response', response?.data);
        if (response.success) {
          dispatch(setApplicationScopes(response?.data?.applications));
          setAllowedMeasurementTypes(response?.data?.allowed_measurement_type);
          initializeFeatureTogglesFromAPI(response?.data?.applications);
        } else {
          dispatch(setError(response.error || 'Failed to fetch application scope'));
        }
      } catch (err: any) {
        dispatch(setError(err.message || 'An unexpected error occurred'));
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchApplicationScope();
  }, [projectId, dispatch, rc]);


  // Initialize feature toggles with prefilled data from API
  const initializeFeatureTogglesFromAPI = (apiData: any[]) => {
    const initialFeatureToggles: Record<string, Record<string, boolean>> = {};
    const initialWidgetToggles: Record<string, Record<string, boolean>> = {};
    const initialViewToggles: Record<string, Record<string, boolean>> = {};
    const initialSmsFeatureToggles: Record<string, { API: boolean; NON_API: boolean }> = {};
    const initialSmsWidgetToggles: Record<string, { API: boolean; NON_API: boolean }> = {};
    const initialSmsViewToggles: Record<string, { API: boolean; NON_API: boolean }> = {};

    const fuelTogglesFromSelectedFor = (selectedFor: any) => ({
      Electric: selectedFor?.electric || false,
      Gas: selectedFor?.gas || false,
      Water: selectedFor?.water || false,
      'Dual Fuel': selectedFor?.dual_fuel || false
    });

    apiData.forEach(app => {
      const sms = isSmsApp(app.id);
      app.features.forEach((feature: any) => {
        if (sms) {
          initialSmsFeatureToggles[smsScopeKey(app.id, feature.name)] = togglesFromSmsSelectedFor(feature.selected_for);
        }
        if (feature.selected_for) {
          initialFeatureToggles[feature.name] = fuelTogglesFromSelectedFor(feature.selected_for);
        }

        if (feature.widgets && Array.isArray(feature.widgets)) {
          feature.widgets.forEach((widget: any) => {
            if (sms) {
              initialSmsWidgetToggles[smsScopeKey(app.id, widget.name)] = togglesFromSmsSelectedFor(widget.selected_for);
            }
            if (widget.selected_for) {
              initialWidgetToggles[widget.name] = fuelTogglesFromSelectedFor(widget.selected_for);
            }

            if (widget.views && Array.isArray(widget.views)) {
              widget.views.forEach((view: any) => {
                if (sms) {
                  initialSmsViewToggles[smsScopeKey(app.id, view.name)] = togglesFromSmsSelectedFor(view.selected_for);
                }
                if (view.selected_for) {
                  initialViewToggles[view.name] = fuelTogglesFromSelectedFor(view.selected_for);
                }
              });
            }
          });
        }
      });
    });

    setFeatureToggles(initialFeatureToggles);
    setWidgetToggles(initialWidgetToggles);
    setViewToggles(initialViewToggles);
    setSmsFeatureToggles(initialSmsFeatureToggles);
    setSmsWidgetToggles(initialSmsWidgetToggles);
    setSmsViewToggles(initialSmsViewToggles);
  };

  const handleAppExpansion = (appId: string) => {
    setExpandedApps(prev => {
      const isOpening = !prev.includes(appId);
      if (isOpening) {
        rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.APP_SCOPE_APP_SELECTED);
      }
      return isOpening
        ? [...prev, appId]
        : prev.filter(id => id !== appId);
    });
  };

  const handleFeatureExpansion = (featureId: string) => {
    setExpandedFeatures(prev => {
      const newSet = new Set(prev);
      if (newSet.has(featureId)) {
        newSet.delete(featureId);
      } else {
        newSet.add(featureId);
      }
      return newSet;
    });
  };

  const handleWidgetExpansion = (widgetId: string) => {
    setExpandedWidgets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(widgetId)) {
        newSet.delete(widgetId);
      } else {
        newSet.add(widgetId);
      }
      return newSet;
    });
  };

  // Helper to get all widgets and views for a feature
  const getFeatureChildren = (appId: string, featureId: string) => {
    const widgets: string[] = [];
    const views: string[] = [];
    const app = applicationScopes.find(a => a.id === appId);
    if (!app) return { widgets, views };
    const feature = app.features.find(f => f.name === featureId);
    if (feature?.widgets) {
      feature.widgets.forEach(widget => {
        widgets.push(widget.name);
        if (widget.views) {
          widget.views.forEach(view => {
            views.push(view.name);
          });
        }
      });
    }
    return { widgets, views };
  };

  const getWidgetChildren = (appId: string, widgetId: string) => {
    const views: string[] = [];
    const app = applicationScopes.find(a => a.id === appId);
    if (!app) return views;
    for (const feature of app.features) {
      if (feature.widgets) {
        for (const widget of feature.widgets) {
          if (widget.name === widgetId && widget.views) {
            widget.views.forEach(view => {
              views.push(view.name);
            });
            return views;
          }
        }
      }
    }
    return views;
  };

  const getParentFeature = (appId: string, widgetId: string) => {
    const app = applicationScopes.find(a => a.id === appId);
    if (!app) return null;
    for (const feature of app.features) {
      if (feature.widgets) {
        for (const widget of feature.widgets) {
          if (widget.name === widgetId) {
            return { featureId: feature.name, widgets: feature.widgets };
          }
        }
      }
    }
    return null;
  };

  const getParentWidget = (appId: string, viewId: string) => {
    const app = applicationScopes.find(a => a.id === appId);
    if (!app) return null;
    for (const feature of app.features) {
      if (feature.widgets) {
        for (const widget of feature.widgets) {
          if (widget.views) {
            for (const view of widget.views) {
              if (view.name === viewId) {
                return { widgetId: widget.name, featureId: feature.name, views: widget.views, allWidgets: feature.widgets };
              }
            }
          }
        }
      }
    }
    return null;
  };

  const handleToggleChange = (appId: string, featureId: string, fuelType: string) => {
    const newValue = !featureToggles[featureId]?.[fuelType];
    const { widgets, views } = getFeatureChildren(appId, featureId);
    
    // Update feature toggle
    setFeatureToggles(prev => ({
      ...prev,
      [featureId]: {
        ...prev[featureId],
        [fuelType]: newValue
      }
    }));
    
    // Cascade to all child widgets
    if (widgets.length > 0) {
      setWidgetToggles(prev => {
        const updated = { ...prev };
        widgets.forEach(widgetId => {
          updated[widgetId] = {
            ...updated[widgetId],
            [fuelType]: newValue
          };
        });
        return updated;
      });
    }
    
    // Cascade to all child views
    if (views.length > 0) {
      setViewToggles(prev => {
        const updated = { ...prev };
        views.forEach(viewId => {
          updated[viewId] = {
            ...updated[viewId],
            [fuelType]: newValue
          };
        });
        return updated;
      });
    }
    
    // Mark that changes have been made
    rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.APP_SCOPE_SETTING_CHANGED);
    setHasChanges(true);
  };

  const handleWidgetToggleChange = (appId: string, widgetId: string, fuelType: string) => {
    const newValue = !widgetToggles[widgetId]?.[fuelType];
    const childViews = getWidgetChildren(appId, widgetId);
    const parentInfo = getParentFeature(appId, widgetId);
    
    // Update widget toggle
    setWidgetToggles(prev => {
      const updated = {
        ...prev,
        [widgetId]: {
          ...prev[widgetId],
          [fuelType]: newValue
        }
      };
      
      // Check if we need to update parent feature
      if (parentInfo) {
        const { featureId, widgets } = parentInfo;
        
        if (newValue) {
          // If turning ON a widget, ensure parent feature is also ON
          setFeatureToggles(prevFeatures => ({
            ...prevFeatures,
            [featureId]: {
              ...prevFeatures[featureId],
              [fuelType]: true
            }
          }));
        } else {
          // If turning OFF, check if all sibling widgets are also OFF
          const allWidgetsOff = widgets.every(w => {
            if (w.name === widgetId) return true; // This one is being turned off
            return !updated[w.name]?.[fuelType];
          });
          
          if (allWidgetsOff) {
            // Turn off parent feature
            setFeatureToggles(prevFeatures => ({
              ...prevFeatures,
              [featureId]: {
                ...prevFeatures[featureId],
                [fuelType]: false
              }
            }));
          }
        }
      }
      
      return updated;
    });
    
    // Cascade to all child views
    if (childViews.length > 0) {
      setViewToggles(prev => {
        const updated = { ...prev };
        childViews.forEach(viewId => {
          updated[viewId] = {
            ...updated[viewId],
            [fuelType]: newValue
          };
        });
        return updated;
      });
    }
    
    // Mark that changes have been made
    rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.APP_SCOPE_SETTING_CHANGED);
    setHasChanges(true);
  };

  const handleViewToggleChange = (appId: string, viewId: string, fuelType: string) => {
    const newValue = !viewToggles[viewId]?.[fuelType];
    const parentInfo = getParentWidget(appId, viewId);
    
    // Update view toggle
    setViewToggles(prev => {
      const updated = {
        ...prev,
        [viewId]: {
          ...prev[viewId],
          [fuelType]: newValue
        }
      };
      
      // Check if we need to update parent widget and feature
      if (parentInfo) {
        const { widgetId, featureId, views, allWidgets } = parentInfo;
        
        if (newValue) {
          // If turning ON a view, ensure parent widget and feature are also ON
          setWidgetToggles(prevWidgets => ({
            ...prevWidgets,
            [widgetId]: {
              ...prevWidgets[widgetId],
              [fuelType]: true
            }
          }));
          setFeatureToggles(prevFeatures => ({
            ...prevFeatures,
            [featureId]: {
              ...prevFeatures[featureId],
              [fuelType]: true
            }
          }));
        } else {
          // If turning OFF, check if all sibling views are also OFF
          const allViewsOff = views.every(v => {
            if (v.name === viewId) return true; // This one is being turned off
            return !updated[v.name]?.[fuelType];
          });
          
          if (allViewsOff) {
            // Turn off parent widget
            setWidgetToggles(prevWidgets => {
              const updatedWidgets = {
                ...prevWidgets,
                [widgetId]: {
                  ...prevWidgets[widgetId],
                  [fuelType]: false
                }
              };
              
              // Check if all widgets are now OFF to turn off parent feature
              const allWidgetsOff = allWidgets.every(w => {
                if (w.name === widgetId) return true; // This one is being turned off
                return !updatedWidgets[w.name]?.[fuelType];
              });
              
              if (allWidgetsOff) {
                setFeatureToggles(prevFeatures => ({
                  ...prevFeatures,
                  [featureId]: {
                    ...prevFeatures[featureId],
                    [fuelType]: false
                  }
                }));
              }
              
              return updatedWidgets;
            });
          }
        }
      }
      
      return updated;
    });
    
    // Mark that changes have been made
    rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.APP_SCOPE_SETTING_CHANGED);
    setHasChanges(true);
  };

  const handleSmsFeatureToggle = (appId: string, featureId: string, columnKey: 'API' | 'NON_API') => {
    const scopeKey = smsScopeKey(appId, featureId);
    const prev = smsFeatureToggles[scopeKey] || { API: false, NON_API: false };
    const newValue = !prev[columnKey];
    const { widgets, views } = getFeatureChildren(appId, featureId);

    setSmsFeatureToggles(p => ({
      ...p,
      [scopeKey]: { ...prev, [columnKey]: newValue }
    }));

    if (widgets.length > 0) {
      setSmsWidgetToggles(p => {
        const u = { ...p };
        widgets.forEach(wid => {
          const wk = smsScopeKey(appId, wid);
          u[wk] = { ...(u[wk] || { API: false, NON_API: false }), [columnKey]: newValue };
        });
        return u;
      });
    }
    if (views.length > 0) {
      setSmsViewToggles(p => {
        const u = { ...p };
        views.forEach(vid => {
          const vk = smsScopeKey(appId, vid);
          u[vk] = { ...(u[vk] || { API: false, NON_API: false }), [columnKey]: newValue };
        });
        return u;
      });
    }
    rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.APP_SCOPE_SETTING_CHANGED);
    setHasChanges(true);
  };

  const handleSmsWidgetToggleChange = (appId: string, widgetId: string, columnKey: 'API' | 'NON_API') => {
    const scopeKey = smsScopeKey(appId, widgetId);
    const prev = smsWidgetToggles[scopeKey] || { API: false, NON_API: false };
    const newValue = !prev[columnKey];
    const childViews = getWidgetChildren(appId, widgetId);
    const parentInfo = getParentFeature(appId, widgetId);

    setSmsWidgetToggles(prev => {
      const updated = {
        ...prev,
        [scopeKey]: { ...(prev[scopeKey] || { API: false, NON_API: false }), [columnKey]: newValue }
      };

      if (parentInfo) {
        const { featureId, widgets } = parentInfo;
        const fk = smsScopeKey(appId, featureId);

        if (newValue) {
          setSmsFeatureToggles(prevFeatures => ({
            ...prevFeatures,
            [fk]: { ...(prevFeatures[fk] || { API: false, NON_API: false }), [columnKey]: true }
          }));
        } else {
          const allWidgetsOff = widgets.every(w => {
            if (w.name === widgetId) return true;
            const wk = smsScopeKey(appId, w.name);
            return !(updated[wk] || { API: false, NON_API: false })[columnKey];
          });

          if (allWidgetsOff) {
            setSmsFeatureToggles(prevFeatures => ({
              ...prevFeatures,
              [fk]: { ...(prevFeatures[fk] || { API: false, NON_API: false }), [columnKey]: false }
            }));
          }
        }
      }

      return updated;
    });

    if (childViews.length > 0) {
      setSmsViewToggles(p => {
        const u = { ...p };
        childViews.forEach(vid => {
          const vk = smsScopeKey(appId, vid);
          u[vk] = { ...(u[vk] || { API: false, NON_API: false }), [columnKey]: newValue };
        });
        return u;
      });
    }
    rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.APP_SCOPE_SETTING_CHANGED);
    setHasChanges(true);
  };

  const handleSmsViewToggleChange = (appId: string, viewId: string, columnKey: 'API' | 'NON_API') => {
    const vKey = smsScopeKey(appId, viewId);
    const newValue = !(smsViewToggles[vKey]?.[columnKey] ?? false);
    const parentInfo = getParentWidget(appId, viewId);

    setSmsViewToggles(prev => {
      const updated = {
        ...prev,
        [vKey]: {
          ...(prev[vKey] || { API: false, NON_API: false }),
          [columnKey]: newValue
        }
      };

      if (parentInfo) {
        const { widgetId, featureId, views, allWidgets } = parentInfo;
        const wKey = smsScopeKey(appId, widgetId);
        const fKey = smsScopeKey(appId, featureId);

        if (newValue) {
          setSmsWidgetToggles(prevWidgets => ({
            ...prevWidgets,
            [wKey]: { ...(prevWidgets[wKey] || { API: false, NON_API: false }), [columnKey]: true }
          }));
          setSmsFeatureToggles(prevFeatures => ({
            ...prevFeatures,
            [fKey]: { ...(prevFeatures[fKey] || { API: false, NON_API: false }), [columnKey]: true }
          }));
        } else {
          const allViewsOff = views.every(v => {
            if (v.name === viewId) return true;
            const vk = smsScopeKey(appId, v.name);
            return !(updated[vk] || { API: false, NON_API: false })[columnKey];
          });

          if (allViewsOff) {
            setSmsWidgetToggles(prevWidgets => {
              const updatedWidgets = {
                ...prevWidgets,
                [wKey]: {
                  ...(prevWidgets[wKey] || { API: false, NON_API: false }),
                  [columnKey]: false
                }
              };

              const allWidgetsOff = allWidgets.every(w => {
                if (w.name === widgetId) return true;
                const wk = smsScopeKey(appId, w.name);
                return !(updatedWidgets[wk] || { API: false, NON_API: false })[columnKey];
              });

              if (allWidgetsOff) {
                setSmsFeatureToggles(prevFeatures => ({
                  ...prevFeatures,
                  [fKey]: {
                    ...(prevFeatures[fKey] || { API: false, NON_API: false }),
                    [columnKey]: false
                  }
                }));
              }

              return updatedWidgets;
            });
          }
        }
      }

      return updated;
    });

    rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.APP_SCOPE_SETTING_CHANGED);
    setHasChanges(true);
  };

  const isSmsScopeEnabled = (
    map: Record<string, { API: boolean; NON_API: boolean }>,
    appId: string,
    name: string,
    columnKey: 'API' | 'NON_API'
  ) => map[smsScopeKey(appId, name)]?.[columnKey] || false;

  const isFeatureEnabled = (featureId: string, fuelType: string) => {
    return featureToggles[featureId]?.[fuelType] || false;
  };

  const isWidgetEnabled = (widgetId: string, fuelType: string) => {
    return widgetToggles[widgetId]?.[fuelType] || false;
  };

  const isViewEnabled = (viewId: string, fuelType: string) => {
    return viewToggles[viewId]?.[fuelType] || false;
  };


  // Get icon mapping for applications (keep for display purposes until API provides icons)
  const getAppIcon = (appId: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'customer-web': <Globe size={20} />,
      'csr-console': <HelpCircle size={20} />,
      'smart-alerts': <Mail size={20} />,
      'smart-sms': <MessageCircle size={20} />,
      'smart-hers': <FileText size={20} />,
      'ev-managed-charging-app': <Zap size={20} />,
      'customer-web-widgets': <Layers size={20} />,
      'customer-web-api': <Link size={20} />,
      'bdr': <Settings size={20} />,
      'sms-alerts': <MessageCircle size={20} />,
      'customer-service-console': <HelpCircle size={20} />,
      'awb-analytics': <BarChart3 size={20} />,
      'system-api': <Sliders size={20} />
    };
    return iconMap[appId] || <Settings size={20} />;
  };
  // Prepare save data from current feature toggles
  const prepareSaveData = (): ApplicationScopeSaveRequest | null => {
    if (!projectId) {
      return null;
    }

    const applications: Array<{
      master_data_id: number;
      allowed_for: FuelTypeAllowance;
    }> = [];
    const visibleFuelTypes = getVisibleFuelTypes();

    const pushSmsScopeRow = (
      master_data_id: number,
      savedSelected: FuelTypeAllowance | undefined,
      fuelToggles: Record<string, boolean> | undefined,
      apiToggles: { API: boolean; NON_API: boolean }
    ) => {
      const saved = savedSelected || {
        electric: false,
        gas: false,
        water: false,
        dual_fuel: false
      };
      const fuel = {
        electric: Boolean(fuelToggles?.['Electric'] ?? saved.electric),
        gas: Boolean(fuelToggles?.['Gas'] ?? saved.gas),
        water: Boolean(fuelToggles?.['Water'] ?? saved.water),
        dual_fuel: Boolean(fuelToggles?.['Dual Fuel'] ?? saved.dual_fuel)
      };
      const currentSelection: FuelTypeAllowance = {
        ...fuel,
        api: apiToggles.API,
        non_api: apiToggles.NON_API
      };
      const hasEnabled =
        apiToggles.API || apiToggles.NON_API ||
        fuel.electric || fuel.gas || fuel.water || fuel.dual_fuel;
      const rowChanged =
        Boolean(apiToggles.API) !== Boolean(saved.api) ||
        Boolean(apiToggles.NON_API) !== Boolean(saved.non_api) ||
        fuel.electric !== Boolean(saved.electric) ||
        fuel.gas !== Boolean(saved.gas) ||
        fuel.water !== Boolean(saved.water) ||
        fuel.dual_fuel !== Boolean(saved.dual_fuel);
      const wasPreviouslyEnabled =
        Boolean(saved.api) || Boolean(saved.non_api) ||
        Boolean(saved.electric) || Boolean(saved.gas) ||
        Boolean(saved.water) || Boolean(saved.dual_fuel);
      if (hasEnabled || rowChanged || (wasPreviouslyEnabled && !hasEnabled)) {
        applications.push({
          master_data_id,
          allowed_for: currentSelection
        });
      }
    };

    // Iterate through all applications and features
    applicationScopes.forEach(app => {
      app.features.forEach(feature => {
        if (isSmsApp(app.id)) {
          const ftKey = smsScopeKey(app.id, feature.name);
          pushSmsScopeRow(
            feature.master_data_id,
            feature.selected_for,
            featureToggles[feature.name],
            smsFeatureToggles[ftKey] ?? togglesFromSmsSelectedFor(feature.selected_for)
          );
          if (feature.widgets && Array.isArray(feature.widgets)) {
            feature.widgets.forEach(widget => {
              const wtKey = smsScopeKey(app.id, widget.name);
              pushSmsScopeRow(
                widget.master_data_id,
                widget.selected_for,
                widgetToggles[widget.name],
                smsWidgetToggles[wtKey] ?? togglesFromSmsSelectedFor(widget.selected_for)
              );
              if (widget.views && Array.isArray(widget.views)) {
                widget.views.forEach(view => {
                  const vtKey = smsScopeKey(app.id, view.name);
                  pushSmsScopeRow(
                    view.master_data_id,
                    view.selected_for,
                    viewToggles[view.name],
                    smsViewToggles[vtKey] ?? togglesFromSmsSelectedFor(view.selected_for)
                  );
                });
              }
            });
          }
          return;
        }

        // Get saved selection with defaults
        const savedSelection = feature.selected_for || {
          electric: false, gas: false, water: false, dual_fuel: false
        };

        // Build current selection: use current toggle state for visible fuel types,
        // preserve saved values for non-visible fuel types
        const currentSelection: FuelTypeAllowance = {
          electric: isFeatureEnabled(feature.name, 'Electric'),
          gas: isFeatureEnabled(feature.name, 'Gas'),
          water: isFeatureEnabled(feature.name, 'Water'),
          dual_fuel: isFeatureEnabled(feature.name, 'Dual Fuel')
        };

        // Check if any visible fuel type toggles are enabled
        const hasEnabledToggles = visibleFuelTypes.some(fuelType =>
          isFeatureEnabled(feature.name, fuelType)
        );

        // Check if current selection differs from saved selection (only for visible fuel types)
        const hasChanges = visibleFuelTypes.some(fuelType => {
          const key = fuelType === 'Electric' ? 'electric' :
                     fuelType === 'Gas' ? 'gas' :
                     fuelType === 'Water' ? 'water' :
                     'dual_fuel';
          return currentSelection[key as keyof typeof currentSelection] !==
                 savedSelection[key as keyof typeof savedSelection];
        });

        // Check if feature was previously enabled (in saved state) and is now disabled
        const wasPreviouslyEnabled = visibleFuelTypes.some(fuelType => {
          const key = fuelType === 'Electric' ? 'electric' :
                     fuelType === 'Gas' ? 'gas' :
                     fuelType === 'Water' ? 'water' :
                     'dual_fuel';
          return savedSelection[key as keyof typeof savedSelection] === true;
        });

        // Save if there are enabled toggles, changes from saved state, or if unselecting a previously enabled feature
        if (hasEnabledToggles || hasChanges || (wasPreviouslyEnabled && !hasEnabledToggles)) {
          applications.push({
            master_data_id: feature.master_data_id,
            allowed_for: currentSelection
          });
        }

        // Process widgets if they exist
        if (feature.widgets && Array.isArray(feature.widgets)) {
          feature.widgets.forEach(widget => {
            const widgetSavedSelection = widget.selected_for || {
              electric: false, gas: false, water: false, dual_fuel: false
            };

            const widgetCurrentSelection: FuelTypeAllowance = {
              electric: isWidgetEnabled(widget.name, 'Electric'),
              gas: isWidgetEnabled(widget.name, 'Gas'),
              water: isWidgetEnabled(widget.name, 'Water'),
              dual_fuel: isWidgetEnabled(widget.name, 'Dual Fuel')
            };

            const widgetHasEnabledToggles = visibleFuelTypes.some(fuelType =>
              isWidgetEnabled(widget.name, fuelType)
            );

            const widgetHasChanges = visibleFuelTypes.some(fuelType => {
              const key = fuelType === 'Electric' ? 'electric' :
                         fuelType === 'Gas' ? 'gas' :
                         fuelType === 'Water' ? 'water' :
                         'dual_fuel';
              return widgetCurrentSelection[key as keyof typeof widgetCurrentSelection] !==
                     widgetSavedSelection[key as keyof typeof widgetSavedSelection];
            });

            // Check if widget was previously enabled (in saved state) and is now disabled
            const widgetWasPreviouslyEnabled = visibleFuelTypes.some(fuelType => {
              const key = fuelType === 'Electric' ? 'electric' :
                         fuelType === 'Gas' ? 'gas' :
                         fuelType === 'Water' ? 'water' :
                         'dual_fuel';
              return widgetSavedSelection[key as keyof typeof widgetSavedSelection] === true;
            });

            if (widgetHasEnabledToggles || widgetHasChanges || (widgetWasPreviouslyEnabled && !widgetHasEnabledToggles)) {
              applications.push({
                master_data_id: widget.master_data_id,
                allowed_for: widgetCurrentSelection
              });
            }

            // Process views if they exist
            if (widget.views && Array.isArray(widget.views)) {
              widget.views.forEach(view => {
                const viewSavedSelection = view.selected_for || {
                  electric: false, gas: false, water: false, dual_fuel: false
                };

                const viewCurrentSelection: FuelTypeAllowance = {
                  electric: isViewEnabled(view.name, 'Electric'),
                  gas: isViewEnabled(view.name, 'Gas'),
                  water: isViewEnabled(view.name, 'Water'),
                  dual_fuel: isViewEnabled(view.name, 'Dual Fuel')
                };

                const viewHasEnabledToggles = visibleFuelTypes.some(fuelType =>
                  isViewEnabled(view.name, fuelType)
                );

                const viewHasChanges = visibleFuelTypes.some(fuelType => {
                  const key = fuelType === 'Electric' ? 'electric' :
                             fuelType === 'Gas' ? 'gas' :
                             fuelType === 'Water' ? 'water' :
                             'dual_fuel';
                  return viewCurrentSelection[key as keyof typeof viewCurrentSelection] !==
                         viewSavedSelection[key as keyof typeof viewSavedSelection];
                });

                // Check if view was previously enabled (in saved state) and is now disabled
                const viewWasPreviouslyEnabled = visibleFuelTypes.some(fuelType => {
                  const key = fuelType === 'Electric' ? 'electric' :
                             fuelType === 'Gas' ? 'gas' :
                             fuelType === 'Water' ? 'water' :
                             'dual_fuel';
                  return viewSavedSelection[key as keyof typeof viewSavedSelection] === true;
                });

                if (viewHasEnabledToggles || viewHasChanges || (viewWasPreviouslyEnabled && !viewHasEnabledToggles)) {
                  applications.push({
                    master_data_id: view.master_data_id,
                    allowed_for: viewCurrentSelection
                  });
                }
              });
            }
          });
        }
      });
    });
    
    if (applications.length === 0) {
      return null;
    }
    
    return {
      project_id: projectId,
      applications
    };
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!hasChanges) {
      return;
    }

    setSaving(true);
    setSaveWarnings([]);
    setSkippedDetails([]);
    setCommunicationEventsError(null);
    setSaveError(null);

    try {
      const saveData = prepareSaveData();

      if (!saveData || saveData.applications.length === 0) {
        toast.warning('No changes to save');
        return;
      }

      const response = await saveApplicationScope(rc, saveData);

      const hasResponseWarnings =
        response.message?.includes('with warnings') ||
        (response.warnings && response.warnings.length > 0);

      if (hasResponseWarnings) {
        toast.warning('Application scope saved with warnings');
        if (response.warnings?.length) {
          setSaveWarnings(response.warnings);
        }
      } else {
        toast.success(`Successfully saved ${response.total_processed} application scope settings`);
      }

      if (response.total_skipped > 0 && response.skipped_details?.length) {
        setSkippedDetails(response.skipped_details);
      }

      if (response.communication_events_updated === false && response.communication_events_error) {
        setCommunicationEventsError(response.communication_events_error);
      }

      rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.APP_SCOPE_SAVED);
      setHasChanges(false);

    } catch (error: any) {
      const message =
        error?.message && error.message !== '[object Object]'
          ? error.message
          : 'Failed to save changes. A server error occurred — please try again.';
      rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.APP_SCOPE_SAVE_FAILED, { error_type: 'api_error', severity: 'critical' });
      toast.error(message);
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  // Map API measurement types to UI fuel type names
  const mapMeasurementTypeToFuelType = (measurementType: string): string => {
    const mapping: Record<string, string> = {
      'ELECTRIC': 'Electric',
      'GAS': 'Gas',
      'WATER': 'Water',
      'DUAL': 'Dual Fuel'
    };
    return mapping[measurementType] || measurementType;
  };

  // Get visible fuel types based on allowed_measurement_types from API response
  const getVisibleFuelTypes = () => {
    const mappedTypes = allowedMeasurementTypes.map(mapMeasurementTypeToFuelType);
    // If length is greater than or equal to 2, add Dual Fuel
    if (allowedMeasurementTypes.length >= 2 && !mappedTypes.includes('Dual Fuel')) {
      return [...mappedTypes, 'Dual Fuel'];
    }
    
    return mappedTypes;
  };

  // Helper function to sort items by sequence, using default index if sequence is null or missing
  const sortBySequence = <T extends { sequence?: number | null }>(
    items: T[]
  ): T[] => {
    // Separate items with sequence and without sequence
    const withSequence: Array<{ item: T; sequence: number; originalIndex: number }> = [];
    const withoutSequence: Array<{ item: T; originalIndex: number }> = [];
    
    items.forEach((item, index) => {
      if (item.sequence != null) {
        withSequence.push({ item, sequence: item.sequence, originalIndex: index });
      } else {
        withoutSequence.push({ item, originalIndex: index });
      }
    });
    
    // Sort items with sequence by their sequence value
    withSequence.sort((a, b) => a.sequence - b.sequence);
    
    // Sort items without sequence by their original index to maintain order
    withoutSequence.sort((a, b) => a.originalIndex - b.originalIndex);
    
    // Combine: items with sequence first, then items without sequence
    return [
      ...withSequence.map(w => w.item),
      ...withoutSequence.map(w => w.item)
    ];
  };

  // Get the display data - use API data directly
  const getDisplayData = () => {
    if (applicationScopes.length === 0) {
      // Return empty array when no API data - don't show static data
      return [];
    }

    // Filter applications that have features with product_key and enhance with display properties
    return applicationScopes
      .map(apiApp => {
        // Sort features by sequence
        const sortedFeatures = sortBySequence(
          apiApp.features.filter(apiFeature => apiFeature.product_key)
        );

        return {
          id: apiApp.id,
          name: apiApp.name,
          icon: getAppIcon(apiApp.id),
          features: sortedFeatures.map(apiFeature => {
            // Sort widgets by sequence
            const sortedWidgets = apiFeature.widgets
              ? sortBySequence(apiFeature.widgets)
              : undefined;

            return {
              id: apiFeature.name,
              name: apiFeature.display_name,
              // tooltip: getFeatureTooltip(apiFeature.name),
              is_allowed: apiFeature.is_allowed,
              allowed_for: apiFeature.allowed_for,
              requirements: apiFeature.requirements,
              widgets: sortedWidgets?.map(apiWidget => {
                // Sort views by sequence
                const sortedViews = apiWidget.views
                  ? sortBySequence(apiWidget.views)
                  : undefined;

                return {
                  name: apiWidget.name,
                  display_name: apiWidget.display_name,
                  is_allowed: apiWidget.is_allowed,
                  allowed_for: apiWidget.allowed_for || {
                    electric: false,
                    gas: false,
                    water: false,
                    dual_fuel: false
                  },
                  requirements: apiWidget.requirements,
                  master_data_id: apiWidget.master_data_id,
                  views: sortedViews?.map(apiView => ({
                    name: apiView.name,
                    display_name: apiView.display_name,
                    is_allowed: apiView.is_allowed,
                    allowed_for: apiView.allowed_for || {
                      electric: false,
                      gas: false,
                      water: false,
                      dual_fuel: false
                    },
                    requirements: apiView.requirements,
                    master_data_id: apiView.master_data_id
                  }))
                };
              })
            };
          })
        };
      })
      .filter(apiApp => apiApp.features.length > 0); // Only show applications that have features with product_key
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* edit project title */}
      <Typography variant="h4" component="h3" style={{ fontSize: '1.8rem', marginBottom: 2, marginLeft:32, textAlign: 'left'}}>
          Edit Project Setup
        </Typography>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => dispatch(clearError())}>
            {error}
          </Alert>
        )}

        {/* Save warnings */}
        {saveWarnings.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setSaveWarnings([])}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Saved with warnings ({saveWarnings.length}):
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {saveWarnings.map((w, i) => {
                const { source, message } = parseSaveWarning(w);
                return (
                  <Box
                    component="li"
                    key={i}
                    sx={{ mb: i < saveWarnings.length - 1 ? 1.5 : 0 }}
                  >
                    {source && (
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                        {source}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {message}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Alert>
        )}

        {/* Skipped items */}
        {skippedDetails.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }} onClose={() => setSkippedDetails([])}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {skippedDetails.length} item{skippedDetails.length > 1 ? 's' : ''} skipped:
            </Typography>
            {skippedDetails.map((d, i) => (
              <Typography key={i} variant="body2">
                {d.type} (id: {d.master_data_id}) — {d.reason}
              </Typography>
            ))}
          </Alert>
        )}

        {/* Save error (5xx / network failures) */}
        {saveError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        {/* Communication events error */}
        {communicationEventsError && (
          <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setCommunicationEventsError(null)}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Communication events not updated</Typography>
            <Typography variant="body2">{communicationEventsError}</Typography>
          </Alert>
        )}


        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Application Scope Section */}
        {!loading && (
          <Card sx={{ mt: 4,px:4, py:2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LayoutGrid size={30} style={{ stroke: theme.palette.primary.main }} />
              <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                Application Scope
              </Typography>
            </Box>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'left' }}>
              Enable and configure the applications and communications for this project across different fuel types.
            </Typography>

            {/* No Data State */}
            {getDisplayData().length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No application scope data available for the current project configuration.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Contact your administrator if you believe this is an error.
                </Typography>
              </Box>
            )}

            {/* Applications List */}
            <CardContent sx={{ display: 'flex',p:0, flexDirection: 'column' }}>
              {getDisplayData().map((app) => {
              const isExpanded = expandedApps.includes(app.id);
              const isSms = isSmsApp(app.id);
              const visibleFuelTypes = getVisibleFuelTypes();
              const scopeColumnCount = isSms
                ? visibleFuelTypes.length + SMS_SCOPE_COLUMNS.length
                : visibleFuelTypes.length;

              return (
                <Box
                  key={app.id}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    mb: 2,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Accordion 
                    expanded={isExpanded}
                    onChange={() => handleAppExpansion(app.id)}
                    sx={{
                      boxShadow: 'none',
                      bgcolor: theme.palette.secondary.main,
                      '&:before': { display: 'none' },
                      '& .MuiAccordionSummary-root': {
                        bgcolor: 'transparent',
                        borderRadius: 0,
                        minHeight: 'auto',
                        '&.Mui-expanded': {
                          minHeight: 'auto',
                        }
                      }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ 
                        px: 3, 
                       
                        py: 2,
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          margin: 0,
                          '&.Mui-expanded': {
                            margin: 0,
                          }
                        },
                        '& .MuiAccordionSummary-expandIconWrapper': {
                          color: theme.palette.text.secondary,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2,  }}>
                        <Box sx={{ color: theme.palette.primary.main }}>
                          {app.icon}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16, color: 'text.primary'}}>
                          {app.name}
                        </Typography>
                        
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails sx={{ p: 0 }}>
                      {/* Table Structure */}
                      <Box sx={{ width: '100%' }}>
                        {/* Table Header */}
                        <Box sx={{ 
                          display: 'grid',
                          gridTemplateColumns: `360px 1fr repeat(${scopeColumnCount}, 100px)`,
                          gap: 2,
                          px: 4,
                          py: 2,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          alignItems: 'center'
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'left' }}>
                            Feature
                          </Typography>
                          <Box></Box>
                          {visibleFuelTypes.map(fuelType => (
                            <Typography key={fuelType} variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'center' }}>
                              {fuelType}
                            </Typography>
                          ))}
                          {isSms && SMS_SCOPE_COLUMNS.map(col => (
                            <Typography key={col.key} variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'center' }}>
                              {col.label}
                            </Typography>
                          ))}
                        </Box>

                        {/* Table Rows */}
                        {app.features.map((feature, index) => {
                          const isFeatureExpanded = expandedFeatures.has(feature.id);
                          const hasWidgets = feature.widgets && feature.widgets.length > 0;
                          
                          return (
                            <Box key={feature.id}>
                              {/* Feature Row */}
                              <Box sx={{ 
                                display: 'grid',
                                gridTemplateColumns: `360px 1fr repeat(${scopeColumnCount}, 100px)`,
                                gap: 2,
                                px: 4,
                                py: 2.5,
                                alignItems: 'center',
                                borderBottom: index < app.features.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                '&:hover': {
                                  bgcolor: theme.palette.secondary.light
                                }
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {hasWidgets && (
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleFeatureExpansion(feature.id)}
                                      sx={{ p: 0.5, color: 'text.secondary' }}
                                    >
                                      {isFeatureExpanded ? (
                                        <ChevronUp size={16} />
                                      ) : (
                                        <ChevronDown size={16} />
                                      )}
                                    </IconButton>
                                  )}
                                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 14, color: 'text.primary', textAlign: 'left' }}>
                                    {feature.name}
                                  </Typography>
                                  <Tooltip 
                                    title={
                                      <Box>
                                        {feature.requirements && (
                                          <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Requirements:</Typography>
                                            <Typography variant="caption" display="block">
                                              Customer: {feature.requirements.customer_type.join(', ')}
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                              Fuel: {feature.requirements.fuel_type.join(', ')}
                                            </Typography>
                                            <Typography variant="caption" display="block">
                                              Meter: {feature.requirements.meter_type.join(', ')}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    } 
                                    arrow
                                  >
                                    <IconButton size="small" sx={{ p: 0.25 }}>
                                      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Box></Box>
                                {visibleFuelTypes.map(fuelType => {
                                  const isEnabled = isFeatureEnabled(feature.id, fuelType);
                                  const isAllowed = isSms
                                    ? true
                                    : Boolean(feature.allowed_for[fuelType.toLowerCase() as keyof FuelTypeAllowance]);

                                  if (!isAllowed) {
                                    return <Box key={fuelType} />;
                                  }

                                  return (
                                    <ScopeSwitchCell
                                      key={fuelType}
                                      checked={isEnabled}
                                      isAllowed={isAllowed}
                                      onChange={() => handleToggleChange(app.id, feature.id, fuelType)}
                                    />
                                  );
                                })}
                                {isSms && SMS_SCOPE_COLUMNS.map(({ key }) => {
                                  const isEnabled = isSmsScopeEnabled(smsFeatureToggles, app.id, feature.id, key);
                                  const isAllowed = isSmsChannelAllowed(feature.allowed_for, key);
                                  return (
                                    <ScopeSwitchCell
                                      key={key}
                                      checked={isEnabled}
                                      isAllowed={isAllowed}
                                      onChange={() => handleSmsFeatureToggle(app.id, feature.id, key)}
                                    />
                                  );
                                })}
                              </Box>
                              
                              {/* Widgets Section - Collapsible */}
                              {hasWidgets && (
                                <Collapse in={isFeatureExpanded}>
                                  <Box sx={{ bgcolor: theme.palette.secondary.dark }}>
                                    {feature.widgets?.map((widget, widgetIndex) => {
                                      const isWidgetExpanded = expandedWidgets.has(widget.name);
                                      const hasViews = widget.views && widget.views.length > 0;
                                      
                                      return (
                                        <Box key={widget.name}>
                                          {/* Widget Row */}
                                          <Box 
                                            sx={{ 
                                              display: 'grid',
                                              gridTemplateColumns: `360px 1fr repeat(${scopeColumnCount}, 100px)`,
                                              gap: 2,
                                              px: 4,
                                              pl: 8, // Indent widgets
                                              py: 2,
                                              alignItems: 'center',
                                              borderBottom: widgetIndex < (feature.widgets?.length || 0) - 1 || hasViews ? `1px solid ${theme.palette.divider}` : 'none',
                                              '&:hover': {
                                                bgcolor: theme.palette.secondary.light
                                              }
                                            }}
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {hasViews && (
                                                <IconButton 
                                                  size="small" 
                                                  onClick={() => handleWidgetExpansion(widget.name)}
                                                  sx={{ p: 0.5, color: 'text.secondary' }}
                                                >
                                                  {isWidgetExpanded ? (
                                                    <ChevronUp size={16} />
                                                  ) : (
                                                    <ChevronDown size={16} />
                                                  )}
                                                </IconButton>
                                              )}
                                              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: 13, color: 'text.secondary', textAlign: 'left' }}>
                                                {widget.display_name || widget.name}
                                              </Typography>
                                            </Box>
                                            <Box></Box>
                                            {visibleFuelTypes.map(fuelType => {
                                              const isEnabled = isWidgetEnabled(widget.name, fuelType);
                                              const isAllowed = isSms
                                                ? true
                                                : Boolean(widget.allowed_for?.[fuelType.toLowerCase() as keyof FuelTypeAllowance]);

                                              if (!isAllowed) {
                                                return <Box key={fuelType} />;
                                              }

                                              return (
                                                <ScopeSwitchCell
                                                  key={fuelType}
                                                  checked={isEnabled}
                                                  isAllowed={isAllowed}
                                                  onChange={() => handleWidgetToggleChange(app.id, widget.name, fuelType)}
                                                />
                                              );
                                            })}
                                            {isSms && SMS_SCOPE_COLUMNS.map(({ key }) => {
                                              const isEnabled = isSmsScopeEnabled(smsWidgetToggles, app.id, widget.name, key);
                                              const isAllowed = isSmsChannelAllowed(widget.allowed_for, key);
                                              return (
                                                <ScopeSwitchCell
                                                  key={key}
                                                  checked={isEnabled}
                                                  isAllowed={isAllowed}
                                                  onChange={() => handleSmsWidgetToggleChange(app.id, widget.name, key)}
                                                />
                                              );
                                            })}
                                          </Box>
                                          
                                          {/* Views Section - Collapsible */}
                                          {hasViews && (
                                            <Collapse in={isWidgetExpanded}>
                                              <Box sx={{ bgcolor: theme.palette.secondary.dark }}>
                                                {widget.views?.map((view, viewIndex) => (
                                                  <Box 
                                                    key={view.name}
                                                    sx={{ 
                                                      display: 'grid',
                                                      gridTemplateColumns: `360px 1fr repeat(${scopeColumnCount}, 100px)`,
                                                      gap: 2,
                                                      px: 4,
                                                      pl: 12, // Indent views more than widgets
                                                      py: 2,
                                                      alignItems: 'center',
                                                      borderBottom: viewIndex < (widget.views?.length || 0) - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                      '&:hover': {
                                                        bgcolor: theme.palette.secondary.light
                                                      }
                                                    }}
                                                  >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                      <Typography variant="body2" sx={{ fontWeight: 400, fontSize: 12, color: 'text.secondary', textAlign: 'left' }}>
                                                        {view.display_name || view.name}
                                                      </Typography>
                                                    </Box>
                                                    <Box></Box>
                                                    {visibleFuelTypes.map(fuelType => {
                                                      const isEnabled = isViewEnabled(view.name, fuelType);
                                                      const isAllowed = isSms
                                                        ? true
                                                        : Boolean(view.allowed_for?.[fuelType.toLowerCase() as keyof FuelTypeAllowance]);

                                                      if (!isAllowed) {
                                                        return <Box key={fuelType} />;
                                                      }

                                                      return (
                                                        <ScopeSwitchCell
                                                          key={fuelType}
                                                          checked={isEnabled}
                                                          isAllowed={isAllowed}
                                                          onChange={() => handleViewToggleChange(app.id, view.name, fuelType)}
                                                        />
                                                      );
                                                    })}
                                                    {isSms && SMS_SCOPE_COLUMNS.map(({ key }) => {
                                                      const isEnabled = isSmsScopeEnabled(smsViewToggles, app.id, view.name, key);
                                                      const isAllowed = isSmsChannelAllowed(view.allowed_for, key);
                                                      return (
                                                        <ScopeSwitchCell
                                                          key={key}
                                                          checked={isEnabled}
                                                          isAllowed={isAllowed}
                                                          onChange={() => handleSmsViewToggleChange(app.id, view.name, key)}
                                                        />
                                                      );
                                                    })}
                                                  </Box>
                                                ))}
                                              </Box>
                                            </Collapse>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Collapse>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              );
            })}
          </CardContent>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={16} />}
              onClick={() => rc.uiRouter.navigate(ROUTE_TREE.PROJECT.DETAILS)}
              sx={{ 
                color: 'text.secondary',
                borderColor: 'divider'
              }}
            >
              Back to Project Details
            </Button>
            
            <PermissionGate require={UI_PAGES.APPLICATION_SCOPE.ctas.save.id} mode="disable">
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                onClick={handleSaveChanges}
                disabled={!hasChanges || saving}
                sx={{
                  bgcolor: hasChanges ? 'primary.main' : 'grey.400',
                  '&:hover': {
                    bgcolor: hasChanges ? 'primary.dark' : 'grey.500',
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500'
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </PermissionGate>
          </Box>
          </Card>
        )}
      </Box>
    </Box>
  );
};